import dayjs from 'dayjs';

/** 日付が未登録または不正な値か（一覧の「未調整」表示用） */
export function isPatientDateUnset(iso: string | null | undefined): boolean {
  if (!iso?.trim()) return true;
  const d = dayjs(iso.slice(0, 10));
  return !d.isValid();
}

/**
 * 初診未完了: 初診日未登録、または初診日が本日（含む）以降。
 * 初診日が本日より前に登録されている場合は完了。
 */
/** 対応期日の表示（YYYY/MM/DD） */
export function formatActionDueDate(iso: string | null | undefined): string | null {
  if (!iso?.trim()) return null;
  const d = dayjs(iso.slice(0, 10));
  if (!d.isValid()) return null;
  return d.format('YYYY/MM/DD');
}

/**
 * 対応期日が迫っているか（期日の1日前以降＝期日当日・前日・超過）。
 */
export function isActionDueDateUrgent(iso: string | null | undefined): boolean {
  if (!iso?.trim()) return false;
  const due = dayjs(iso.slice(0, 10)).startOf('day');
  if (!due.isValid()) return false;
  const today = dayjs().startOf('day');
  const warningFrom = due.subtract(1, 'day');
  return !today.isBefore(warningFrom, 'day');
}

export function isFirstVisitIncomplete(firstVisitDate: string | null | undefined): boolean {
  if (!firstVisitDate?.trim()) return true;
  const d = dayjs(firstVisitDate.slice(0, 10));
  if (!d.isValid()) return true;
  const today = dayjs().startOf('day');
  return !d.startOf('day').isBefore(today, 'day');
}

/** 生年月日を西暦＋和暦（昭和・平成・令和）で表示 */
export function formatBirthDateWithEra(dob: string | null, ageSuffix?: string | null): string | null {
  if (!dob?.trim()) return null;
  const d = dayjs(dob.slice(0, 10));
  if (!d.isValid()) return null;

  const western = d.format('YYYY/MM/DD');
  let japanese = '';
  try {
    japanese = new Intl.DateTimeFormat('ja-JP', {
      era: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      calendar: 'japanese',
    }).format(d.toDate());
  } catch {
    japanese = '';
  }

  const base = japanese ? `${western}（${japanese}）` : western;
  return ageSuffix ? `${base} ${ageSuffix}` : base;
}

/** 満年齢のみ */
export function formatPatientAgeYears(dob: string | null): string | null {
  if (!dob?.trim()) return null;
  const d = dayjs(dob.slice(0, 10));
  if (!d.isValid()) return null;
  return `満${dayjs().diff(d, 'year')}歳`;
}

/** 生年月日＋和暦＋満年齢（1行・患者詳細・カルテメモ等） */
export function formatPatientAgeSingleLine(dob: string | null): string | null {
  return formatBirthDateWithEra(dob, formatPatientAgeYears(dob));
}

/** 生年月日＋和暦（1行目）と満年齢（2行目）（患者一覧カード等） */
export function formatPatientAge(dob: string | null): string | null {
  const birth = formatBirthDateWithEra(dob);
  const age = formatPatientAgeYears(dob);
  if (!birth && !age) return null;
  if (!birth) return age;
  if (!age) return birth;
  return `${birth}\n${age}`;
}
